import { useCallback } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import ConfirmModal from '#client/components/ConfirmModal';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useToastContext from '#client/hooks/useToastContext';
import { DELETE_COUNTER_PARTY_MUTATION } from '#client/mutations';

export default function ConfirmDeletePayeeModal({
  id,
  firstName,
  handleSuccess = () => {},
  handleClose
}) {
  const { getCustomerToken, loading: customerTokenLoading } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });
  const navigate = useNavigate();

  const { createSuccessToast, createErrorToast } = useToastContext();

  const [deleteCounterparty, { loading: loadingDeletePayee }] = useMutation(
    DELETE_COUNTER_PARTY_MUTATION,
    {
      noThrow: true
    }
  );

  /**
   * handle delete payee
   */
  const handleDeletePayee = useCallback(async () => {
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      navigate('', { replace: true });
      return;
    }

    const { error: deletePayeeError } = await deleteCounterparty({
      variables: {
        input: {
          counterpartyId: id,
          customerToken
        }
      },
      skip: loadingDeletePayee
    });

    if (deletePayeeError) {
      // Show toast error
      createErrorToast({
        title: 'Payee was not updated',
        description: 'There was an error updating this payee. Please try again.',
        error: deletePayeeError
      });
    } else {
      createSuccessToast({
        title: `${firstName} is deleted`,
        description: 'This payee is no longer in your available payees'
      });
      handleSuccess();
    }
  }, [
    getCustomerToken,
    deleteCounterparty,
    id,
    loadingDeletePayee,
    navigate,
    createErrorToast,
    createSuccessToast,
    firstName,
    handleSuccess
  ]);

  return (
    <ConfirmModal
      cancelText="Cancel"
      handleClose={handleClose}
      handleOK={handleDeletePayee}
      loading={loadingDeletePayee || customerTokenLoading}
      message="This action cannot be undone."
      name={firstName}
      okText="Delete payee"
      title={`Are you sure you want to delete ${firstName}?`}
    />
  );
}

ConfirmDeletePayeeModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  handleSuccess: PropTypes.func
};
